import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

function isSchemaError(error) {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [memberships, setMemberships] = useState([])
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error loading profile:', error?.message);
            throw error;
          } else if (error?.code === 'PGRST116') {
            console.log('No profile found for user');
          } else {
            console.log('Profile load error:', error?.message);
          }
        } else {
          setUserProfile(data)
          // Load memberships
          await profileOperations?.loadMemberships(userId);
        }
      } catch (error) {
        console.error('Profile load error:', error)
        throw error;
      } finally {
        setProfileLoading(false)
      }
    },

    async loadMemberships(userId) {
      try {
        const { data, error } = await supabase
          ?.from('center_memberships')
          ?.select('*, centers(id, name, city)')
          ?.eq('user_id', userId);
        if (!error && data) {
          setMemberships(data);
        }
      } catch (err) {
        console.log('Memberships load error:', err?.message);
      }
    },

    clear() {
      setUserProfile(null)
      setMemberships([])
      setProfileLoading(false)
    }
  }

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        profileOperations?.load(session?.user?.id)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })?.catch((err) => {
      console.error('Session check error:', err?.message)
      setLoading(false)
    })

    let subscription = null
    try {
      let result = supabase?.auth?.onAuthStateChange(authStateHandlers?.onChange)
      subscription = result?.data?.subscription
    } catch (err) {
      console.error('Auth state change setup error:', err?.message)
      setLoading(false)
    }

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    if (!supabase || !supabase?.auth) {
      return { data: null, error: { message: 'Authentication service is not available. Please check your connection and try again.' } };
    }
    return new Promise((resolve) => {
      let settled = false;
      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve({ data: null, error: { message: 'Request timed out. Please check your internet connection and try again.' } });
        }
      }, 15000);

      const handleError = (err) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          console.error('Sign in error:', err?.message);
          const isNetworkError =
            err instanceof TypeError ||
            err?.name === 'TypeError' || err?.message?.includes('Failed to fetch') ||
            err?.message?.includes('NetworkError') ||
            err?.message?.includes('network') ||
            err?.message?.includes('fetch') ||
            err?.message?.includes('Load failed');
          const message = isNetworkError
            ? 'Unable to connect to the server. Please check your internet connection and try again.' : err?.message || 'An error occurred. Please try again.';
          resolve({ data: null, error: { message } });
        }
      };

      try {
        const promise = supabase.auth.signInWithPassword({ email, password });
        if (promise && typeof promise.then === 'function') {
          promise
            .then((result) => {
              if (!settled) {
                settled = true;
                clearTimeout(timeout);
                if (result?.error) {
                  resolve({ data: null, error: result.error });
                } else {
                  resolve({ data: result?.data ?? null, error: null });
                }
              }
            })
            .catch(handleError);
        } else {
          handleError(new Error('Authentication service returned an unexpected response.'));
        }
      } catch (syncErr) {
        handleError(syncErr);
      }
    });
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: { data: metadata }
      })
      if (error && isSchemaError(error)) throw error;
      return { data, error }
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (error && isSchemaError(error)) throw error;
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (error && isSchemaError(error)) throw error;
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // ─── Role helpers ─────────────────────────────────────────────────────────
  const isSuperAdmin = () =>
    userProfile?.global_role === 'super_admin' || userProfile?.role === 'super_admin';

  const hasRole = (role) => userProfile?.role === role;

  const hasAnyRole = (roles) => roles?.includes(userProfile?.role);

  const isAdmin = () => hasRole('admin');
  const isCenterManager = () => hasRole('center_manager');
  const isSales = () => hasRole('sales');

  // Get center IDs the user has membership for
  const getUserCenterIds = () => memberships?.map(m => m?.center_id) || [];

  // Check if user has membership for a specific center
  const hasCenterAccess = (centerId) =>
    isSuperAdmin() || memberships?.some(m => m?.center_id === centerId);

  // Get membership role for a specific center
  const getCenterRole = (centerId) =>
    memberships?.find(m => m?.center_id === centerId)?.role || null;

  // Determine the routing prefix based on role
  const getRoutePrefix = () => {
    if (isSuperAdmin()) return '/sa';
    if (isAdmin()) return '/admin';
    return '/app';
  };

  const value = {
    user,
    userProfile,
    memberships,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    hasRole,
    hasAnyRole,
    isAdmin,
    isCenterManager,
    isSales,
    isSuperAdmin,
    getUserCenterIds,
    hasCenterAccess,
    getCenterRole,
    getRoutePrefix,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
